import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CreditCard, Plus, Check } from 'lucide-react';
import { PaymentMethod } from '@generated/proto/model';

interface PaymentMethodSelectorProps {
  paymentMethods: PaymentMethod[];
  selectedPaymentMethod: PaymentMethod | null;
  onSelect: (paymentMethod: PaymentMethod) => void;
  onUseNewCard: () => void;
  useNewCard: boolean;
  onCancelNewCard: () => void;
}

export function PaymentMethodSelector({
  paymentMethods,
  selectedPaymentMethod,
  onSelect,
  onUseNewCard,
  useNewCard,
  onCancelNewCard,
}: PaymentMethodSelectorProps) {
  const getCardBrandIcon = (brand: string) => {
    // You could add specific brand icons here
    return <CreditCard className="h-4 w-4" />;
  };

  return (
    <div className="space-y-3">
      {/* Existing Payment Methods */}
      {paymentMethods.map((paymentMethod) => (
        <Card
          key={paymentMethod.paymentMethodId}
          className={`p-3 cursor-pointer border-2 transition-colors ${
            selectedPaymentMethod?.paymentMethodId === paymentMethod.paymentMethodId
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => onSelect(paymentMethod)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getCardBrandIcon(paymentMethod.card?.brand || '')}
              <div>
                <div className="font-medium">
                  {paymentMethod.card?.brand?.toUpperCase() || 'Card'} ••••{paymentMethod.card?.last4}
                </div>
                <div className="text-sm text-gray-500">
                  Expires {paymentMethod.card?.expMonth}/{paymentMethod.card?.expYear}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {paymentMethod.isDefault && (
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                  Default
                </span>
              )}
              {selectedPaymentMethod?.paymentMethodId === paymentMethod.paymentMethodId && (
                <Check className="h-5 w-5 text-blue-500" />
              )}
            </div>
          </div>
        </Card>
      ))}

      {/* New Card Option */}
      <Card
        className={`p-3 cursor-pointer border-2 transition-colors ${
          useNewCard
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3" onClick={useNewCard ? onCancelNewCard : onUseNewCard}>
            <Plus className="h-4 w-4" />
            <span className="font-medium">
              {useNewCard ? 'Cancel new card' : 'Use a new card'}
            </span>
          </div>
          {useNewCard && <Check className="h-5 w-5 text-blue-500" />}
        </div>
      </Card>

      {paymentMethods.length === 0 && !useNewCard && (
        <div className="text-center py-6 text-gray-500">
          <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No payment methods saved.</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={onUseNewCard}
          >
            Add a payment method
          </Button>
        </div>
      )}
    </div>
  );
}